import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../../lib/api";
import { PAGE_ROLES, ROLE_LABEL } from "../../lib/roles";
import { BrandLogo } from "../BrandLogo";
import {
  Gauge, Kanban, Calculator, Users, Wrench, HardDrives, Sparkle,
  CaretLeft, CaretRight, CaretDown, IdentificationCard, CalendarBlank, SignOut, ShieldCheck,
  Buildings, FileText, Airplane, Clock, Receipt, UsersFour, MapPin, ShieldStar,
  MagnifyingGlass, ListChecks, Briefcase, Database, Gear,
} from "@phosphor-icons/react";

// Grouped nav. Command Centre stays as a top-level pinned item (user explicitly
// asked to keep it that way); everything else is grouped under broader
// categories so the sidebar reads like a product menu, not an index of pages.
const NAV_GROUPS = [
  {
    pinned: true,
    items: [
      { to: "/", label: "Command Centre", icon: Gauge, testid: "nav-dashboard" },
    ],
  },
  {
    key: "sales",
    label: "Sales & Delivery",
    icon: Briefcase,
    items: [
      { to: "/leads",      label: "Leads",      icon: MagnifyingGlass, testid: "nav-leads" },
      { to: "/clients",    label: "Clients",    icon: Buildings,       testid: "nav-clients" },
      { to: "/proposals",  label: "Proposals",  icon: FileText,        testid: "nav-proposals" },
      { to: "/pipeline",   label: "Pipeline",   icon: Kanban,          testid: "nav-pipeline" },
      { to: "/timeline",   label: "Timeline",   icon: Clock,           testid: "nav-timeline" },
      { to: "/milestones", label: "Milestones", icon: Receipt,         testid: "nav-milestones" },
      { to: "/pricing",    label: "Pricing / BoQ", icon: Calculator,   testid: "nav-pricing" },
    ],
  },
  {
    key: "people",
    label: "People & Scheduling",
    icon: Users,
    items: [
      { to: "/personnel",   label: "Personnel",         icon: IdentificationCard, testid: "nav-personnel" },
      { to: "/talent",      label: "Talent Pool",       icon: Users,              testid: "nav-talent" },
      { to: "/talent-map",  label: "Talent Map",        icon: MapPin,             testid: "nav-talent-map" },
      { to: "/roster",      label: "Roster",            icon: CalendarBlank,      testid: "nav-roster" },
      { to: "/schedule",    label: "Schedule",          icon: CalendarBlank,      testid: "nav-schedule" },
      { to: "/field-techs", label: "Field Technicians", icon: Wrench,             testid: "nav-fieldtechs" },
      { to: "/timesheets",  label: "Timesheets",        icon: Clock,              testid: "nav-timesheets" },
      { to: "/site-access", label: "Site Access",       icon: ShieldStar,         testid: "nav-site-access" },
      { to: "/leave",       label: "Leave",             icon: Airplane,           testid: "nav-leave" },
    ],
  },
  {
    key: "reference",
    label: "Reference Data",
    icon: Database,
    items: [
      { to: "/catalog", label: "Hardware Catalog", icon: HardDrives, testid: "nav-catalog" },
      { to: "/insights", label: "AI Hiring Insights", icon: Sparkle, testid: "nav-insights" },
      { to: "/backlog", label: "Backlog", icon: ListChecks, testid: "nav-backlog" },
    ],
  },
  {
    key: "settings",
    label: "Settings",
    icon: Gear,
    items: [
      { to: "/team", label: "Team Access", icon: UsersFour, testid: "nav-team" },
    ],
  },
];

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  // All non-pinned groups expanded by default. Keyed state so each group collapses independently.
  const [openGroups, setOpenGroups] = useState({ sales: true, people: true, reference: true, settings: true });
  const { user, logout } = useAuth();
  const role = user?.role;

  // Filter each group's items by the SAME PAGE_ROLES map the backend & ProtectedRoute use;
  // drop empty groups entirely so users don't see headers with nothing under them.
  const visibleGroups = NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((item) => (PAGE_ROLES[item.to] || []).includes(role)),
  })).filter((g) => g.items.length > 0);

  const renderItem = (n) => (
    <NavLink
      key={n.to}
      to={n.to}
      end={n.to === "/"}
      data-testid={n.testid}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-md text-[13px] font-medium border ${
          isActive
            ? "bg-[#FFC107] border-[#FFC107] text-[#0F1E38]"
            : "border-transparent text-slate-300 hover:text-white hover:bg-white/5"
        }`
      }
      style={{ transition: "background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease" }}
    >
      <n.icon size={17} weight="duotone" />
      {!collapsed && <span>{n.label}</span>}
    </NavLink>
  );

  return (
    <aside
      data-testid="app-sidebar"
      className={`${collapsed ? "w-[72px]" : "w-[248px]"} shrink-0 h-screen sticky top-0 bg-[#0F1E38] text-white flex flex-col`}
      style={{ transition: "width 0.25s ease" }}
    >
      <div className="h-20 flex items-center px-4 border-b border-white/10">
        <BrandLogoWhite variant={collapsed ? "icon" : "full"} />
      </div>

      <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
        {visibleGroups.map((g, idx) => {
          if (g.pinned) {
            return (
              <div key={`pinned-${idx}`} className="space-y-1">
                {g.items.map(renderItem)}
                {!collapsed && <div className="h-px bg-white/10 mx-2 my-2" />}
              </div>
            );
          }
          const open = openGroups[g.key] !== false;
          return (
            <div key={g.key} className="mt-2">
              {!collapsed ? (
                <button
                  type="button"
                  data-testid={`navgroup-${g.key}`}
                  onClick={() => setOpenGroups({ ...openGroups, [g.key]: !open })}
                  className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.18em] text-slate-400 hover:text-white"
                >
                  <span className="flex items-center gap-2">
                    <g.icon size={13} weight="duotone" />
                    {g.label}
                  </span>
                  <CaretDown size={11} weight="bold" style={{ transform: open ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform 0.15s ease" }} />
                </button>
              ) : (
                <div className="h-px bg-white/5 my-2" />
              )}
              {open && <div className="space-y-0.5">{g.items.map(renderItem)}</div>}
            </div>
          );
        })}
      </nav>

      {user && !collapsed && (
        <div data-testid="user-chip" className="mx-2 mb-2 p-3 border border-white/10 rounded-md bg-white/[0.03]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#FFC107] text-[#0F1E38] flex items-center justify-center font-mono text-xs font-bold">{(user.name || user.email || "?")[0]?.toUpperCase()}</div>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-white truncate">{user.name || user.email}</div>
              <div className="flex items-center gap-1 mt-0.5">
                <ShieldCheck size={10} className={role === "admin" ? "text-emerald-400" : "text-amber-400"} />
                <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">{ROLE_LABEL[role] || role}</span>
              </div>
            </div>
            <button data-testid="logout-btn" onClick={logout} className="text-slate-400 hover:text-red-400" style={{transition:"color 0.15s ease"}}><SignOut size={14} /></button>
          </div>
        </div>
      )}

      <button
        data-testid="sidebar-collapse-toggle"
        onClick={() => setCollapsed((c) => !c)}
        className="m-2 px-3 py-2 flex items-center gap-2 justify-center text-xs text-slate-400 hover:text-white border border-white/10 rounded-md"
        style={{ transition: "color 0.15s ease" }}
      >
        {collapsed ? <CaretRight size={14} /> : <><CaretLeft size={14} /> Collapse</>}
      </button>
    </aside>
  );
}

// White variant for navy sidebar background — uses the full official lockup logo.
function BrandLogoWhite({ variant = "full" }) {
  if (variant === "icon") {
    return (
      <div className="w-10 h-10 rounded-md overflow-hidden bg-black border border-white/20 flex items-center justify-center">
        <img
          src="/soverenx-logo.jpg"
          alt="SoverenX"
          className="h-full w-auto max-w-none object-cover object-left"
          style={{ transform: "scale(2.3)", transformOrigin: "left center" }}
        />
      </div>
    );
  }
  return (
    <div className="flex items-center">
      <img
        src="/soverenx-logo.jpg"
        alt="SoverenX Systems"
        className="h-11 w-auto object-contain"
      />
    </div>
  );
}
